import type { Brand, GuidelineLayout } from "@/types/brand";
import { defineStore } from "pinia";

interface BrandState {
  brands: Brand[];
  selectedBrand: Brand | null;
}

export const useBrandStore = defineStore("brand", {
  state: (): BrandState => ({
    brands: [],
    selectedBrand: null,
  }),

  getters: {
    // Layouts from the selected brand's published guidelines; empty means the fixed layout list.
    guidelineLayouts: (state): GuidelineLayout[] => state.selectedBrand?.guidelineLayouts ?? [],

    // The picked guideline layout if the selected brand still publishes it, else its first one (the
    // backend falls back the same way); null when the brand has no guideline layouts.
    guidelineLayoutIdOrFirst: (state) => (pickedId: string | null): string | null => {
      const layouts = state.selectedBrand?.guidelineLayouts ?? [];
      if (layouts.some((layout) => layout.id === pickedId)) return pickedId;
      return layouts[0]?.id ?? null;
    },
  },

  actions: {
    setBrands(brands: Brand[]) {
      this.brands = brands;
    },
    setSelectedBrand(brand: Brand | null) {
      this.selectedBrand = brand;
    },

    // LOGO ASSETS
    addLogoAsset(asset: string) {
      if (!this.selectedBrand) return;
      this.selectedBrand.logoAssets.push(asset);
      if (!this.selectedBrand.selectedLogoAssets.includes(asset)) {
        this.selectedBrand.selectedLogoAssets.push(asset);
      }
    },
    removeLogoAsset(url: string) {
      if (!this.selectedBrand) return;
      this.selectedBrand.logoAssets = this.selectedBrand.logoAssets.filter(
        (a) => a !== url
      );
    },

    // HERO ASSETS
    addHeroAsset(asset: string) {
      if (!this.selectedBrand) return;
      this.selectedBrand.heroAssets.push(asset);
      if (!this.selectedBrand.selectedHeroAssets.includes(asset)) {
        this.selectedBrand.selectedHeroAssets.push(asset);
      }
    },
    removeHeroAsset(url: string) {
      if (!this.selectedBrand) return;
      this.selectedBrand.heroAssets = this.selectedBrand.heroAssets.filter((a) => a !== url);
      this.selectedBrand.selectedHeroAssets = this.selectedBrand.selectedHeroAssets.filter((a) => a !== url);
    },
    addSelectedHeroAsset(asset: string) {
      if (!this.selectedBrand) return;
      if (!this.selectedBrand.selectedHeroAssets.includes(asset))
        this.selectedBrand.selectedHeroAssets.push(asset);
    },
    removeSelectedHeroAsset(url: string) {
      if (!this.selectedBrand) return;
      this.selectedBrand.selectedHeroAssets = this.selectedBrand.selectedHeroAssets.filter((a) => a !== url);
    },

    // BACKGROUND ASSETS
    addBackgroundAsset(asset: string) {
      if (!this.selectedBrand) return;
      this.selectedBrand.backgroundAssets.push(asset);
      if (!this.selectedBrand.selectedBackgroundAssets.includes(asset)) {
        this.selectedBrand.selectedBackgroundAssets.push(asset);
      }
    },
    removeBackgroundAsset(url: string) {
      if (!this.selectedBrand) return;
      this.selectedBrand.backgroundAssets = this.selectedBrand.backgroundAssets.filter(
        (a) => a !== url
      );
    },
    setSelectedColorName(name: string | null) {
      if (!this.selectedBrand) return;
      this.selectedBrand.selectedColorName = name;
    },
    addColor(color: string) {
      this.selectedBrand?.colors.push(color);
    },
    removeColor(color: string) {
      if (!this.selectedBrand) return;
      this.selectedBrand.colors = this.selectedBrand.colors.filter(
        (c) => c !== color
      );
    },
    reset() {
      this.brands = [];
      this.selectedBrand = null;
    },
  },
});
